// src/components/OrdersPage/OrdersPage.jsx
import React, { useEffect, useState } from "react";
import { authFetch } from "../api/api";
import OrderFormModal from "./OrderFormModal";
import Button from "../UI/Button";
import Card from "../UI/Card";
import { useNavigate } from "react-router-dom";
import Modal from "../UI/Modal";
import ConfirmModal from "../UI/ConfirmModal";
import { useTranslation } from "react-i18next";


const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;



const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const [modalOrder, setModalOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  const pendingOrders = orders.filter(o => o.status === "pending");
  const completedOrders = orders.filter(o => o.status === "completed");

  const completedCardClass = "bg-gray-100 opacity-70";

  const { t } = useTranslation("OrdersPage");


  const loadOrders = async () => {
    setLoading(true);

    try {
      const res = await authFetch(`${BASE_URL}/api/orders/`);
      if (!res.ok) throw new Error(`${t("error")} ${res.status}`);

      const data = await res.json();
      setOrders(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  

  const openModal = (order = null) => {
    setModalOrder(order);
    setShowModal(true);
  };

  const closeModal = () => {
    setModalOrder(null);
    setShowModal(false);
  };

  const handleDelete = async () => {
    if (!orderToDelete) return;

    await authFetch(
      `${BASE_URL}/api/orders/${orderToDelete.id}/`,
      {
        method: "DELETE",
      }
    );

    setOrders((prev) =>
      prev.filter((o) => o.id !== orderToDelete.id)
    );

    setShowDeleteConfirm(false);
    setOrderToDelete(null);
  };

  if (loading) {
    return (
      <div className="w-full max-w-5xl mx-auto mt-10">
        <p>{t("loading")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-5xl mx-auto mt-10">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto mt-10 space-y-6">
      {/* HEADER */}
      <div className="flex justify-between items-center"> 
        <h1 className="text-2xl font-bold">{t("orders")}</h1>

        <Button
          variant="primary"
          onClick={() => openModal(null)}
        >
          {t("add_order")}
        </Button>
      </div>

      {/* LIST */}
      <div className="space-y-4">
        <p>{t("current_orders")}</p>
        {pendingOrders.map((order) => {
          const items = order.items || [];

          const clientTotal = items.reduce(
            (sum, i) => sum + Number(i.client_price || 0),
            0
          );

          const internalTotal = items.reduce(
            (sum, i) => sum + Number(i.purchase_price || 0),
            0
          );

          const profit = (
            clientTotal +
            Number(order.price_to_expeditor || 0) -
            internalTotal -
            Number(order.price_delivery || 0)
          ).toFixed(2);

         

          return (
            <Card
              key={order.id}
              hoverable
              className="overflow-hidden cursor-pointer"
              onClick={() => navigate(`/orders/${order.id}`)}
            >
              {/* HEADER */}
              <div
                className="flex justify-between items-start cursor-pointer"
              >
                <div className="space-y-1">
                  <p className="font-semibold text-lg">
                    {order.order_code}
                  </p>

                  <p className="text-gray-600">
                    {order.client_data || "-"}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-medium">
                    {t("profit")}: {order.profit || "-"}¥
                  </p>
                  <p className="font-medium">
                    {t("start_date")}: {order.start_date || "-"}
                  </p>
                  <p><b>{t("tracking")}:</b> {order.expeditor_tracking || "-"}</p>
                  
            {items.length === 0 ? (
                      <p className="text-gray-500">{t("no_products")}</p>
                    ) : (
                      <div className="space-y-3">
                        {items.map((item) => {
                          const p = item.product_data || {};

                          return (
                            <div 
                              key={item.id} 
                              className="border rounded p-3 space-y-1"
                            >
                              <p className="font-semibold">
                                {p.name_en || "-"} / {p.name_cn || "-"}
                                
                              </p>

                              <p className="text-sm text-gray-700">
                                {t("quantity")}: {item.quantity}
                              </p>

                           

                            </div>
                          );
                        })}
                      </div>
                    )}
                  
                </div>
                <div className="text-right">
                  <p className="font-medium">
                    {t("commission_rate")}: {order.commission_rate}%
                  </p>
                  
                </div>
                <div className="flex gap-2">
                  <Button
                  variant="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(order);
                  }}
                  >
                    {t("edit")}
                  </Button>

                  <Button
                  variant="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOrderToDelete(order);
                    setShowDeleteConfirm(true);
                    }}
                  >
                    {(t("delete"))}
                  </Button>


                </div>

              </div>

            </Card>);
        })}
      </div> 


      <div className="space-y-4">
        <p>{t("completed_orders")}</p>
        {completedOrders.map((order) => {
          const items = order.items || [];

          const clientTotal = items.reduce(
            (sum, i) => sum + Number(i.client_price || 0),
            0
          );

          const internalTotal = items.reduce(
            (sum, i) => sum + Number(i.purchase_price || 0),
            0
          );

          const profit = (
            clientTotal +
            Number(order.price_to_expeditor || 0) -
            internalTotal -
            Number(order.price_delivery || 0)
          ).toFixed(2);

         

          return (
            <Card
              key={order.id}
              hoverable
              className={`overflow-hidden cursor-pointer ${completedCardClass}`}
              onClick={() => navigate(`/orders/${order.id}`)}
            >
              {/* HEADER */}
              <div
                className="flex justify-between items-start cursor-pointer"
              >
                <div className="space-y-1">
                  <p className="font-semibold text-lg">
                    {order.order_code}
                  </p>

                  <p className="text-gray-600">
                    {order.client_data || "-"}
                  </p>
                </div>

                <div className="text-right">
                  <p className="font-medium">
                    {t("profit")}: {order.profit || "-"}¥
                  </p>
                  
                </div>
                <div className="text-right">
                  <p className="font-medium">
                    {t("commission_rate")}: {order.commission_rate}%
                  </p>
                  
                </div>
                <div className="flex gap-2">
                  <Button
                  variant="edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    openModal(order);
                  }}
                  >
                    Редактировать
                  </Button>

                  <Button
                  variant="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOrderToDelete(order);
                    setShowDeleteConfirm(true);
                    }}
                  >
                    {t("delete")}
                  </Button>


                </div>

              </div>

            </Card>);
        })}
      </div> 

              

      {/* MODAL */}
      {showModal && (
        <OrderFormModal
          order={modalOrder}
          onClose={closeModal}
          onSave={loadOrders}
        />
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setOrderToDelete(null); 
          
        }}
        onConfirm={handleDelete}
        title={t("confirm_delete_title")}
        description={
        <>
          {t("confirm_delete_description")}
        </>
        }
        confirmText={t("delete")}
      />
      


    </div>
    
  );
};

export default OrdersPage;